import type { CSSProperties, FC } from 'react';
import { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { TrafficLightConfigType } from '@pp/core/src/components/game/trafficLight/types';
import { L10nText } from '@pp/core/src/services/uiTranslations/reactBindings/L10nText';
import { useGameStateSlice } from '@pp/core/src/store/slices/game/selectors';
import { gameStateActions } from '@pp/core/src/store/slices/game/slice';
import { GameStatus } from '@pp/core/src/store/slices/game/types';
import { useSettingsSlice } from '@pp/core/src/store/slices/settings/selectors';
import { useModifiers } from '@pp/core/src/styles/hooks/useModifiers';

import { useGameDispatch } from '../../store/GameStore';
import { useHighflyerStateSlice } from '../../store/slices/highflyerState/selectors';
import { recentResultDetailStateActions } from '../../store/slices/recentResultDetail/slice';

import styles from './CrashTrafficLight.module.css';

export interface ICrashTrafficLightStatus extends TrafficLightConfigType {
  calculatedScheduledStatusChange?: {
    nextStatus: string;
    timeout: number;
  };
}

interface TrafficLightViewProps {
  animationDuration?: number;
  /**
   * Enables fallback animation with "left" instead of translateX. This is due to a bug in iOS Chrome LCE-64741
   */
  fallbackAnimation?: boolean;
  /**
   *  Pauses the timer and animation. Used in game help and storybook
   */
  isPaused?: boolean;
  onScheduledStatusChange: (nextStatus: string) => void;
  progressOffset?: number;
  status: ICrashTrafficLightStatus;
  timerStartTime: number;
  resetAnimationDurationCallback?: () => void;
  line?: boolean;
  floatingStatusBar?: boolean;
}

export const CrashTrafficLight = forwardRef<HTMLDivElement, TrafficLightViewProps>(
  ({ animationDuration = 0, fallbackAnimation, isPaused = false, onScheduledStatusChange, status, timerStartTime, resetAnimationDurationCallback, line, floatingStatusBar,
  }) => {
    const dispatch = useGameDispatch();
    const animationDurationInSeconds = useMemo(() => Math.round(animationDuration / 1_000), [animationDuration]);
    const [currentTimer, setCurrentTimer] = useState<number>(0); // in seconds
    const loopRef = useRef<number>();
    const currentStatus = useGameStateSlice.getCurrentStatus();
    const isSingleBetPlaced = useHighflyerStateSlice.isSingleBetPlaced();
    const currentLanguage = useSettingsSlice.currentLanguage();
    const langMods = useModifiers(styles, 'crashTrafficLight__label', {
      lang: currentLanguage === 'hi',
    });
    const handleTimerExpiration = useCallback(() => {
      if (!loopRef.current) return;
      window.clearTimeout(loopRef.current);
      loopRef.current = undefined;
      resetAnimationDurationCallback?.();
    }, [resetAnimationDurationCallback]);

    const mods = useModifiers(styles, 'crashTrafficLight', {
      fallbackAnimation,
      isAnimated: animationDuration !== 0 && status.rendersTimer,
      isPaused: isPaused || (animationDuration == 0 && status.rendersTimer),
      line,
    });

    useEffect(() => {
      const { calculatedScheduledStatusChange } = status;
      if (calculatedScheduledStatusChange && animationDuration > 0) {
        let timeout = calculatedScheduledStatusChange.timeout; // -5000
        const wholeTimerDuration = animationDuration - (Date.now() - timerStartTime);
        if (timeout < 0) {
          timeout = wholeTimerDuration + timeout;
        }
        const timeoutId = window.setTimeout(() => {
          onScheduledStatusChange(calculatedScheduledStatusChange.nextStatus);
        }, timeout);
        return (): void => {
          window.clearTimeout(timeoutId);
        };
      }
    }, [status, isPaused, onScheduledStatusChange, animationDuration, timerStartTime]);

    const handleTimeout = useCallback((): void => {
      if (!animationDurationInSeconds) return;
      const elapsedMilliSeconds = Date.now() - timerStartTime;
      const elapsedSeconds = elapsedMilliSeconds / 1_000;
      const newTimerValue = Math.round(animationDurationInSeconds - elapsedSeconds);

      const handleTimerUpdate = (newTimer: number, elapsedSeconds: number): void => {
        setCurrentTimer(newTimer);
        dispatch(recentResultDetailStateActions.setTimeRemaining(newTimer));
        const timeUntilNextSecond = (Math.ceil(elapsedSeconds) - elapsedSeconds) * 1_000;
        loopRef.current = window.setTimeout(handleTimeout, timeUntilNextSecond);
      };
      if (newTimerValue <= 1) {
        setTimeout(() => {
          dispatch(gameStateActions.setCurrentGameStatus(GameStatus.BetsClosed));
        }, 500);
      }

      if (newTimerValue <= 0) {
        handleTimerExpiration();
        setCurrentTimer(0);
        dispatch(recentResultDetailStateActions.setTimeRemaining(0));
      } else {
        handleTimerUpdate(newTimerValue, elapsedSeconds);
      }
    }, [animationDurationInSeconds, handleTimerExpiration, timerStartTime]);

    useEffect(() => {
      if (status.rendersTimer) {
        if (!loopRef.current && animationDurationInSeconds > 0) {
          setCurrentTimer(animationDurationInSeconds);
          dispatch(recentResultDetailStateActions.setTimeRemaining(animationDurationInSeconds));
          loopRef.current = window.setTimeout(handleTimeout, 1_000);
        }
      } else if (loopRef.current) {
        handleTimerExpiration();
        setCurrentTimer(0);
        dispatch(recentResultDetailStateActions.setTimeRemaining(0));
      }
      if (isPaused) {
        setCurrentTimer(animationDurationInSeconds);
        dispatch(recentResultDetailStateActions.setTimeRemaining(animationDurationInSeconds));
        window.clearTimeout(loopRef.current);
      }
    }, [
      status.rendersTimer,
      animationDurationInSeconds,
      timerStartTime,
      handleTimeout,
      isPaused,
      handleTimerExpiration,
    ]);

    const floatingMods = useModifiers(styles, 'crashTrafficLightFloating', {
      floatingBetBoard: floatingStatusBar,
    });

    return (
      <div className={`${styles.crashTrafficLight} ${mods}`}>
        <div
          data-testid={status.label}
          className={`${styles.crashTrafficLight__label} ${floatingMods} ${langMods}`}
          dir={status.isLTR !== undefined && status.isLTR ? 'ltr' : undefined}
          style={
            {
              '--trafficLightVisibility': `var(--game-traffic-light-${status.style})`,
            } as CSSProperties
          }
        >
          <TrafficLightLabel
            config={status}
            timer={currentTimer}
            currentStatus={currentStatus}
            isSingleBetPlaced={isSingleBetPlaced}
          />
        </div>
      </div>
    );
  }
);

const TrafficLightLabel: FC<{
  config: TrafficLightConfigType;
  timer: number | null;
  currentStatus: string;
  isSingleBetPlaced: boolean;
}> = ({ config: Config, timer, currentStatus, isSingleBetPlaced }) => {
  const mods = useModifiers(styles, 'crashTrafficLightLabel', {
    danger: [GameStatus.BetsRejected].includes(currentStatus as GameStatus),
  });

  return (
    <>
      <span className={`${mods}`}>
        {Config.label ? (
          <L10nText tk={isSingleBetPlaced ? 'BET_ACCEPTED_CAPS' : Config.label} dynamicValues={Config.attrs} />
        ) : null}
      </span>
      {Config.rendersTimer ? <TrafficLightTimer timer={timer} config={Config} /> : null}
    </>
  );
};

const TrafficLightTimer: FC<{ config: TrafficLightConfigType; timer: number | null }> = ({ config, timer }) => {
  if (!timer) return;

  return (
    <>
      <span className={styles.crashTrafficLight__counterSeparator}>&nbsp;</span>
      <span className={styles.crashTrafficLight__counter}>
        <span
          className={styles.crashTrafficLight__counterValue}
          style={
            {
              '--trafficLightFontColor': `var(--game-traffic-light-color-${config.style})`,
            } as CSSProperties
          }
        >
          {timer}
          <L10nText tk="NXT_GAME_10" />
        </span>
      </span>
    </>
  );
};

CrashTrafficLight.displayName = 'CrashTrafficLight';
